import { type Tokens, useUserCurrentRetrieve } from "@innoclouds/api";
import { useQueryClient } from "@tanstack/vue-query";
import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";
import { useAuth } from "#/composables/useAuth";

export const useUserStore = defineStore("user", () => {
	const queryClient = useQueryClient();
	const { accessToken, refreshToken, setTokens, clear } = useAuth();

	const { data, isLoading, error, queryKey } = useUserCurrentRetrieve({
		query: {
			enabled: () => !!accessToken.value,
			retry: false,
			refetchOnReconnect: true,
			staleTime: 300_000, // 5 minutes
		},
	});

	const isAuthorized = computed(() => !!data.value);
	const isInitialized = ref(false);

	const initAuth = async () => {
		if (isInitialized.value) return;

		if (accessToken.value) {
			if (isLoading.value) {
				await new Promise<void>((resolve) => {
					const unwatch = watch(isLoading, (loading) => {
						if (!loading) {
							unwatch();
							resolve();
						}
					});
				});
			}
		}
		isInitialized.value = true;
	};

	const invalidate = async () => {
		await queryClient.invalidateQueries({ queryKey });
	};

	const auth = async (tokens?: Tokens | null | false) => {
		if (!tokens) return;
		if (accessToken.value !== tokens.access || refreshToken.value !== tokens.refresh) {
			setTokens(tokens);
			await invalidate();
		}

		return data.value;
	};

	const logout = async () => {
		clear();
		queryClient.resetQueries({ queryKey });
		// @TODO: Проверить что сброс работает корректно и чистит как надо
	};

	watch(accessToken, (newAccessToken, oldAccessToken) => {
		if (!newAccessToken && oldAccessToken) logout();
		if (newAccessToken !== oldAccessToken) invalidate();
	});

	return {
		data,
		error,
		isAuthorized,
		isLoading,
		isInitialized, 
		queryKey,
		initAuth,
		auth,
		logout,
		invalidate,
	};
});
